import { format, subDays } from 'date-fns'
import { api } from '../../../api/api'
import db from '../../../db/db'
import { formatDateToISOShort } from '../../../common/utilities/FormatDate'

// Safety cap on pagination loops — this tool runs manually against a live
// backend, it must never spin into an unbounded loop against production.
// Kept generous (5k records) because visits/invitations batch fetches now
// page from a distant epoch rather than from `startDate` (see BATCH_SYNC_EPOCH
// below), so a low cap could truncate before ever reaching the target day on
// accounts with a large history.
const MAX_PAGES = 50
const PAGE_SIZE = 100

// `min_updated_at` on the batch endpoints is a delta-sync cursor (records
// touched since this timestamp), NOT a "belongs to this day" filter. A visit
// or invitation created days ago and never modified since would be silently
// skipped if we seeded the cursor with `startDate`. To get a true point-in-time
// snapshot for comparison we always pull from the same distant sentinel
// SyncEngine uses for a first-time full sync, then filter by day client-side.
const BATCH_SYNC_EPOCH = '2000-01-01'

export const DEFAULT_SAMPLE_PREFIXES = ['A', 'B', 'C', 'D', 'E', 'M', 'P', 'R', 'S', 'T']

export function normalizeStr(value) {
  return (value ?? '').toString().trim().toLowerCase()
}

function valuesEqual(a, b, type) {
  if (type === 'date') {
    const ta = a ? new Date(a).getTime() : null
    const tb = b ? new Date(b).getTime() : null
    if (ta == null || tb == null || Number.isNaN(ta) || Number.isNaN(tb)) {
      return normalizeStr(a) === normalizeStr(b)
    }
    return ta === tb
  }
  if (type === 'number') {
    return Number(a || 0) === Number(b || 0)
  }
  return normalizeStr(a) === normalizeStr(b)
}

/**
 * Generic set-diff for two normalized record lists matched by `keyField`.
 * Used for entities where BOTH the legacy and batch endpoints return a full
 * (or comparably-bounded) dataset — visits and invitations.
 *
 * @param {object[]} legacyRecords records normalized from the `master` (ground-truth) endpoint
 * @param {object[]} batchRecords  records normalized from the offline batch endpoint
 * @param {string}   keyField      field used to match a record across both sets
 * @param {Array<{field: string, type?: 'string'|'date'|'number'}>} compareFields
 */
export function diffRecordSets(legacyRecords, batchRecords, keyField, compareFields) {
  const legacyMap = new Map(legacyRecords.map(r => [r[keyField], r]))
  const batchMap = new Map(batchRecords.map(r => [r[keyField], r]))

  const onlyInLegacy = []
  const onlyInBatch = []
  const mismatched = []
  let matched = 0

  for (const [key, legacyRecord] of legacyMap) {
    const batchRecord = batchMap.get(key)
    if (!batchRecord) {
      onlyInLegacy.push(legacyRecord)
      continue
    }
    matched++
    for (const { field, type = 'string' } of compareFields) {
      if (!valuesEqual(legacyRecord[field], batchRecord[field], type)) {
        mismatched.push({ key, field, legacyValue: legacyRecord[field], batchValue: batchRecord[field] })
      }
    }
  }

  for (const [key, batchRecord] of batchMap) {
    if (!legacyMap.has(key)) onlyInBatch.push(batchRecord)
  }

  return { onlyInLegacy, onlyInBatch, matched, mismatched }
}

/**
 * Subset check: every item returned by a legacy SEARCH endpoint must exist
 * in the batch/offline CATALOG dataset. Used for comes_from and employees,
 * where the legacy endpoint only returns partial (prefix-matched) results
 * and can never be diffed 1:1 against the full batch dataset.
 */
export function diffSubset(legacyItems, batchItems, matchKeyFn) {
  const batchSet = new Set(batchItems.map(matchKeyFn))
  const missingFromBatch = legacyItems.filter(item => !batchSet.has(matchKeyFn(item)))
  return {
    legacyCount: legacyItems.length,
    batchCount: batchItems.length,
    missingFromBatch,
    isSubset: missingFromBatch.length === 0,
  }
}

function isWithinDay(dateStr, startDate, endDate) {
  if (!dateStr) return false
  const t = new Date(dateStr).getTime()
  if (Number.isNaN(t)) return false
  const start = new Date(startDate).getTime()
  const end = new Date(endDate).getTime() + (24 * 60 * 60 * 1000) - 1
  return t >= start && t <= end
}

// ---------------------------------------------------------------------------
// Visits: GET /visits (legacy, master) vs GET /visits/batch (offline, develop)
// ---------------------------------------------------------------------------

function normalizeVisit(v) {
  return {
    id: v.id ?? null,
    identification: v.identification || '',
    first_name: v.first_name || '',
    last_name_one: v.last_name_one || '',
    last_name_two: v.last_name_two || '',
    entry_time: v.entry_time || '',
    exit_time: v.exit_time || '',
    comes_from: v.comes_from || '',
    notes: v.notes || '',
    card_number: v.card_number || '',
    license_plate: v.license_plate || '',
  }
}

async function fetchLegacyVisits({ startDate, endDate }) {
  let page = 0
  let total = Infinity
  const results = []
  while (page * PAGE_SIZE < total && page < MAX_PAGES) {
    const { data } = await api.get('/visits', {
      params: { prefix: '', page: page + 1, page_size: PAGE_SIZE, destination_ids: [] }
    })
    if (!data?.success) break
    results.push(...(data.visits || []))
    total = data.total ?? results.length
    page++
  }
  const truncated = page >= MAX_PAGES && page * PAGE_SIZE < total
  const records = results.filter(v => isWithinDay(v.entry_time, startDate, endDate)).map(normalizeVisit)
  return { records, truncated }
}

async function fetchBatchVisits({ startDate, endDate }) {
  // `since_date` filtra por `entry_time` en el servidor y evita recorrer todo
  // el historial. Va un día antes de `startDate` porque `isWithinDay` toma
  // `startDate` como medianoche UTC, que en la zona del tenant puede caer la
  // tarde anterior: así el servidor no recorta nada que el filtro de abajo
  // (el mismo que se aplica al legacy) conservaría.
  const sinceDate = format(subDays(new Date(`${startDate}T00:00:00`), 1), 'yyyy-MM-dd')
  let checkpoint = BATCH_SYNC_EPOCH
  let hasMore = true
  let pages = 0
  const results = []
  while (hasMore && pages < MAX_PAGES) {
    const { data } = await api.get('/visits/batch', {
      params: { min_updated_at: checkpoint, limit: PAGE_SIZE, since_date: sinceDate },
    })
    if (!data?.success) break
    results.push(...(data.visits || []))
    hasMore = data.hasMore ?? false
    if (data.lastCheckpoint && data.lastCheckpoint !== checkpoint) checkpoint = data.lastCheckpoint
    else hasMore = false
    pages++
  }
  const truncated = pages >= MAX_PAGES && hasMore
  const records = results.filter(v => isWithinDay(v.entry_time, startDate, endDate)).map(normalizeVisit)
  return { records, truncated }
}

/**
 * NOTE: /visits/batch is fetched from BATCH_SYNC_EPOCH (not `startDate`) and
 * filtered by `entry_time` client-side — see BATCH_SYNC_EPOCH's comment for
 * why seeding the cursor with `startDate` would silently drop records created
 * before the window but dated inside it. On accounts with very large
 * histories this pull can still hit MAX_PAGES before reaching the target
 * day; that case is surfaced via `truncated` rather than silently
 * under-reporting. `since_date` va pegado al día consultado (no a la ventana
 * local de 90 días): este check valida el endpoint batch del backend para un
 * día cualquiera, no la réplica local podada, así que no depende de
 * `SYNC_WINDOW_DAYS` (see docs/local-first.md, sección 5c). Con `since_date`
 * el servidor ya no recorre el historial completo, así que MAX_PAGES casi
 * nunca se alcanza.
 */
export async function runVisitsParityCheck({ startDate, endDate }) {
  const [legacy, batch] = await Promise.all([
    fetchLegacyVisits({ startDate, endDate }),
    fetchBatchVisits({ startDate, endDate }),
  ])
  const compareFields = [
    { field: 'identification' },
    { field: 'first_name' },
    { field: 'last_name_one' },
    { field: 'last_name_two' },
    { field: 'entry_time', type: 'date' },
    { field: 'exit_time', type: 'date' },
    { field: 'comes_from' },
    { field: 'notes' },
    { field: 'card_number' },
    { field: 'license_plate' },
  ]
  const diff = diffRecordSets(legacy.records, batch.records, 'id', compareFields)
  return {
    ...diff,
    legacyCount: legacy.records.length,
    batchCount: batch.records.length,
    truncated: legacy.truncated || batch.truncated,
  }
}

// ---------------------------------------------------------------------------
// Invitations: GET /invitations (legacy grid rows) vs GET /invitations/batch
// ---------------------------------------------------------------------------

// Legacy /invitations returns "grid" rows: an array of cell objects indexed
// positionally, mirroring what master's PreRegisters.js `clearInvitations`
// used to do before the offline rewrite (see docs/local-first.md history).
function normalizeLegacyInvitationRow(row) {
  const day = row?.[0]?.value || ''
  const full_name = (row?.[2]?.value || '').trim()
  const identification = row?.[3]?.value || ''
  const comes_from = row?.[4]?.value || ''
  const company = row?.[5]?.value || ''
  const host = row?.[6]?.value || ''
  const notes = row?.[8]?.value || ''
  return {
    key: `${normalizeStr(identification)}|${day}|${normalizeStr(full_name)}`,
    day, full_name, identification, comes_from, company, host, notes,
  }
}

function normalizeBatchInvitation(inv) {
  // `invitation_date` ('YYYY-MM-DD') is the real business date the backend
  // and Dexie use (see docs/local-first.md, sección 5c) — matches the
  // '%Y-%m-%d' format the legacy grid row already uses for row[0].value, so
  // no reformatting is needed for the composite match key to line up.
  const entryDate = inv.invitation_date || inv.date || inv.entry_time || inv.scheduled_date || ''
  const day = entryDate ? String(entryDate).split('T')[0] : ''
  const firstName = inv.first_name || ''
  const lastName = inv.last_name_one || inv.last_name || ''
  const lastName2 = inv.last_name_two || ''
  const full_name = `${firstName} ${lastName} ${lastName2}`.trim()
  const company = inv.destination?.name || inv.company || ''
  const host = inv.user?.name || inv.host || ''
  const identification = inv.identification || ''
  return {
    key: `${normalizeStr(identification)}|${day}|${normalizeStr(full_name)}`,
    day, full_name, identification,
    comes_from: inv.comes_from || '',
    company, host, notes: inv.notes || '',
  }
}

async function fetchLegacyInvitations({ startDate, endDate }) {
  let page = 0
  let total = Infinity
  const results = []
  while (page * PAGE_SIZE < total && page < MAX_PAGES) {
    const { data } = await api.get('/invitations', {
      params: { start_date: startDate, end_date: endDate, prefix: '', page: page + 1, page_size: PAGE_SIZE }
    })
    if (!data?.success) break
    results.push(...(data.items || []))
    total = data.total ?? results.length
    page++
  }
  return results.map(normalizeLegacyInvitationRow)
}

/**
 * `/invitations/batch` now has two modes distinguished by the presence of
 * `start_date`/`end_date` (see docs/local-first.md, sección 5c):
 *  - "hoy" (both blank): always returns today's complete set, ignores
 *    `min_updated_at`/`limit` entirely.
 *  - "ventana" (both present): filters by `invitation_date` in
 *    [start_date, end_date], supports real `limit`/`hasMore` pagination and
 *    `min_updated_at` for incremental pages.
 * This parity check always audits an explicit day range chosen by the user,
 * so it MUST always request "ventana" mode — omitting start_date/end_date
 * here would silently collapse every check into "today only", regardless of
 * the range selected in the UI.
 */
async function fetchBatchInvitations({ startDate, endDate }) {
  let checkpoint = null
  let hasMore = true
  let pages = 0
  const results = []
  while (hasMore && pages < MAX_PAGES) {
    const params = { start_date: startDate, end_date: endDate, limit: PAGE_SIZE }
    if (checkpoint) params.min_updated_at = checkpoint
    const { data } = await api.get('/invitations/batch', { params })
    if (!data?.success) break
    results.push(...(data.invitations || []).filter(i => !i._delete))
    hasMore = data.hasMore ?? false
    if (data.lastCheckpoint && data.lastCheckpoint !== checkpoint) checkpoint = data.lastCheckpoint
    else hasMore = false
    pages++
  }
  // Backend already filters by invitation_date within range — the client-side
  // filter is kept only as defense-in-depth against a malformed response.
  return results.map(normalizeBatchInvitation).filter(r => isWithinDay(r.day, startDate, endDate))
}

/**
 * NOTE: the legacy grid response has no stable server id, so records are
 * matched by a composite key (identification + day + full name). Two
 * distinct visitors sharing all three on the same day would collide — an
 * accepted limitation given the legacy endpoint's shape, surfaced in the UI.
 *
 * This check compares the two BACKEND endpoints directly for an arbitrary
 * user-chosen range — it never touches Dexie/IndexedDB, so it is NOT scoped
 * by the local 90-day sync window (see docs/local-first.md, sección 5c). A
 * non-empty `onlyInLegacy`/`onlyInBatch` here always points to a real backend
 * discrepancy for that range, not to local replica pruning. To audit the
 * local replica's coverage of "today" specifically — the one case that must
 * have zero tolerance because `VisitForm` depends on it offline — use
 * `runInvitationsTodayParityCheck` instead.
 */
export async function runInvitationsParityCheck({ startDate, endDate }) {
  const [legacy, batch] = await Promise.all([
    fetchLegacyInvitations({ startDate, endDate }),
    fetchBatchInvitations({ startDate, endDate }),
  ])
  const compareFields = [
    { field: 'comes_from' },
    { field: 'company' },
    { field: 'host' },
    { field: 'notes' },
  ]
  const diff = diffRecordSets(legacy, batch, 'key', compareFields)
  return { ...diff, legacyCount: legacy.length, batchCount: batch.length }
}

/**
 * Critical-path check: invitations for TODAY must be an *exact* match
 * between legacy and the batch endpoint's "hoy" mode — this is the only
 * dataset `VisitForm`'s offline autocomplete actually depends on (see
 * docs/local-first.md, sección 5c), so unlike the date-range check above,
 * any `onlyInLegacy`/`onlyInBatch`/`mismatched` here represents a real
 * regression in the critical pull, never an expected side effect of the
 * 90-day window (today is always exempt from `pruneOldRecords`).
 *
 * Always audits the current calendar day server-side (`Time.zone.now`,
 * matching the backend's own "hoy" resolution) regardless of whatever range
 * is selected in the parity panel's date pickers.
 */
export async function runInvitationsTodayParityCheck() {
  const todayISO = formatDateToISOShort(new Date())
  const [legacy, batch] = await Promise.all([
    fetchLegacyInvitations({ startDate: todayISO, endDate: todayISO }),
    (async () => {
      const { data } = await api.get('/invitations/batch', { params: {} })
      const items = data?.success ? (data.invitations || []).filter(i => !i._delete) : []
      return items.map(normalizeBatchInvitation)
    })(),
  ])
  const compareFields = [
    { field: 'comes_from' },
    { field: 'company' },
    { field: 'host' },
    { field: 'notes' },
  ]
  const diff = diffRecordSets(legacy, batch, 'key', compareFields)
  return { ...diff, legacyCount: legacy.length, batchCount: batch.length, auditedDate: todayISO }
}

// ---------------------------------------------------------------------------
// Comes from: GET /visits/comes/:prefix (search) vs GET /visits/comes/all
// ---------------------------------------------------------------------------

/**
 * `db.comes_from` (populated by `pullComesFrom()`) now holds a bounded
 * top-N (default 500, by frequency/recency) instead of the full historical
 * catalog — an intentional design choice (see docs/local-first.md, sección
 * 5c), not a regression of BUG-02. A sampled legacy name missing from that
 * local set is therefore EXPECTED for long-tail, low-frequency values and
 * does not by itself indicate a sync defect — `isSubset: false` here should
 * be read as "some sampled names fall outside the local top-N", not as a
 * hard failure. A real regression would instead show up as `batchCount`
 * collapsing far below the expected top-N size (e.g. back to double digits,
 * as previously seen with BUG-02).
 */
export async function runComesFromParityCheck({ samplePrefixes } = {}) {
  const prefixes = samplePrefixes?.length ? samplePrefixes : DEFAULT_SAMPLE_PREFIXES
  const namesSet = new Set()
  for (const prefix of prefixes) {
    try {
      const { data } = await api.get(`visits/comes/${prefix}`)
      if (data?.success) (data.comes || []).forEach(name => namesSet.add(normalizeStr(name)))
    } catch (err) {
      // A single failed prefix shouldn't abort the whole sample
      console.warn('[OfflineAudit] comes_from sample prefix failed:', prefix, err.message)
    }
  }
  const legacyNames = [...namesSet].map(name => ({ name }))
  const batchRows = await db.comes_from.toArray()
  const diff = diffSubset(legacyNames, batchRows, row => normalizeStr(row.name))
  return { ...diff, sampledPrefixes: prefixes }
}

// ---------------------------------------------------------------------------
// Black list: GET /black_list/search/:identification (point lookup) vs
// GET /black_lists (full offline catalog)
// ---------------------------------------------------------------------------

const BLACKLIST_CONTROL_IDENTIFICATION = 'AUDIT-CONTROL-0000-NOT-LISTED'

export async function runBlackListParityCheck({ sampleIdentifications } = {}) {
  const localRows = await db.black_lists.toArray()
  const localSet = new Set(localRows.map(r => normalizeStr(r.identification)))

  const identifications = sampleIdentifications?.length
    ? sampleIdentifications
    : [...localRows.slice(0, 5).map(r => r.identification), BLACKLIST_CONTROL_IDENTIFICATION]

  const checked = []
  for (const identification of identifications) {
    let legacyHit = false
    try {
      const { data } = await api.get(`black_list/search/${identification}`)
      legacyHit = !!data?.success
    } catch (err) {
      legacyHit = false
    }
    const batchHit = localSet.has(normalizeStr(identification))
    checked.push({ identification, legacyHit, batchHit, match: legacyHit === batchHit })
  }

  const mismatches = checked.filter(r => !r.match)
  return { checked, mismatches, isConsistent: mismatches.length === 0 }
}

// ---------------------------------------------------------------------------
// Employees: GET /employees/search?prefix= vs GET /employees (dataKey users)
// ---------------------------------------------------------------------------

export async function runEmployeesParityCheck({ samplePrefixes, enabledVisitHost } = {}) {
  if (!enabledVisitHost) {
    return { skipped: true }
  }
  const prefixes = samplePrefixes?.length ? samplePrefixes : DEFAULT_SAMPLE_PREFIXES.slice(0, 5)
  const legacyMap = new Map()
  for (const prefix of prefixes) {
    try {
      const { data } = await api.get('employees/search', { params: { prefix } })
      if (data?.success) (data.users || []).forEach(u => legacyMap.set(u.id, u))
    } catch (err) {
      console.warn('[OfflineAudit] employees sample prefix failed:', prefix, err.message)
    }
  }
  const legacyUsers = [...legacyMap.values()]
  const batchUsers = await db.employees.toArray()
  const diff = diffSubset(legacyUsers, batchUsers, u => u.id)
  return { ...diff, sampledPrefixes: prefixes }
}
